import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { TopicContent } from "./TopicContent";
import { todayIso } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, FileText, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

const blank = () => ({
  topic_id: null,
  job_id: "",
  talk_date: todayIso(),
  start_time: "",
  location: "",
  title: "",
  overview: "",
  discussion_points: [],
  site_specific_hazards: "",
  site_specific_controls: "",
  work_activity: "",
  notes: "",
  expected_attendee_ids: [],
  status: "scheduled",
});

export function CreateTalkDialog({ open, onOpenChange, jobs, users, topics, categories, onCreated, initialJobId }) {
  const [selected, setSelected] = useState([]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(blank());
  const [cat, setCat] = useState("ALL");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setStep(1); setForm({...blank(), job_id: initialJobId || new URLSearchParams(window.location.search).get("job_id") || ""}); setSelected([]); setCat("ALL"); setQ(""); }
  }, [open, initialJobId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const shownTopics = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (topics || []).filter((t) =>
      (cat === "ALL" || t.category === cat) &&
      (!ql || [t.title, t.category, t.short_description].filter(Boolean).some((v) => v.toLowerCase().includes(ql)))
    );
  }, [topics, cat, q]);

  const pickTopic = t => setSelected(items => items.some(item => item.topic_id === t.id)
    ? items.filter(item => item.topic_id !== t.id)
    : [...items, { topic_id: t.id, key: t.id, title: t.title, category: t.category, content: t, attachments: t.attachments || [] }]);
  const startBlank = () => {
    setSelected(items => [...items, { key: crypto.randomUUID(), topic_id: null, title: "", category: "General", overview: "", discussion_points: [] }]);
    setStep(2);
  };
  const editCustom = (index, key, value) => setSelected(items => items.map((item, i) => i === index ? {...item, [key]: value} : item));
  const move = (index, direction) => setSelected(items => {
    const next = [...items]; [next[index], next[index + direction]] = [next[index + direction], next[index]]; return next;
  });

  const toggleAttendee = (id) => {
    setForm((f) => ({
      ...f,
      expected_attendee_ids: f.expected_attendee_ids.includes(id)
        ? f.expected_attendee_ids.filter((x) => x !== id)
        : [...f.expected_attendee_ids, id],
    }));
  };

  const submit = async () => {
    if (!form.job_id) return toast.error("Pick a job for this talk");
    if (!selected.length) return toast.error("Select at least one topic");
    if (selected.some(t => !t.topic_id && !t.title.trim())) return toast.error("Give each custom topic a title");
    setSaving(true);
    try {
      const body = {
        ...form,
        title: form.title.trim() || (selected.length === 1 ? selected[0].title : `Toolbox Talk — ${selected.length} topics`),
        topics: selected.map(t => t.topic_id ? { topic_id: t.topic_id } : { title: t.title, category: t.category, overview: t.overview, discussion_points: t.discussion_points }),
      };
      const { data } = await api.post("/tbx/talks", body);
      toast.success("Toolbox talk created");
      onOpenChange(false);
      onCreated?.(data.id);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not create talk. Check the details and retry.");
    } finally {
      setSaving(false);
    }
  };

  const fieldWorkers = (users || []).filter((u) => u.role !== "super_admin");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-[#0D141C] border-zinc-200 dark:border-white/12" data-testid="create-talk-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase tracking-tight font-display text-2xl">
            {step === 1 ? "Select toolbox topics" : "Talk details"}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Select one or more library topics for one session. Your selections stay selected while you search or change category."
              : "Review the topic order, then set the job, date and crew. One signature covers this complete session."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search toolbox library..."
                  className="pl-9" data-testid="talk-topic-search" />
              </div>
              <Button variant="outline" onClick={startBlank} className="rounded-sm uppercase text-xs font-bold tracking-wider" data-testid="talk-start-blank">
                <FileText className="w-4 h-4 mr-2" /> Add custom topic
              </Button>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {["ALL", ...new Set([...(categories || []), ...(topics || []).map(t => t.category).filter(Boolean)])].map((c) => (
                <button key={c} onClick={() => setCat(c)}
                  className={`px-3 py-3 rounded text-[11px] uppercase tracking-wider font-bold border transition-colors ${
                    cat === c ? "bg-[#FF5F15] border-[#FF5F15] text-white"
                      : "border-zinc-300 dark:border-white/15 text-zinc-600 dark:text-zinc-300 hover:border-[#FF5F15]"}`}
                  data-testid={`talk-cat-${c}`}>
                  {c}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-2 max-h-[46vh] overflow-y-auto pr-1">
              {shownTopics.map((t) => (
                <label key={t.id} className={`flex gap-3 text-left p-3 rounded border cursor-pointer ${selected.some(item => item.topic_id === t.id) ? "border-[#FF5F15] bg-orange-50 dark:bg-orange-500/10" : "border-zinc-200 dark:border-white/10"}`} data-testid={`talk-topic-${t.id}`}>
                  <input type="checkbox" className="w-6 h-6 shrink-0 accent-[#FF5F15] mt-1" checked={selected.some(item => item.topic_id === t.id)} onChange={() => pickTopic(t)} />
                  <span className="min-w-0"><span className="block text-[10px] uppercase text-zinc-500">{t.category}</span><strong className="block text-sm">{t.title}</strong><span className="block text-xs text-zinc-500 mt-1">{t.short_description}</span></span>
                </label>
              ))}
              {shownTopics.length === 0 && (
                <div className="col-span-full text-center text-sm text-zinc-500 py-8">No topics match.</div>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <section className="space-y-2"><h3 className="text-xs font-bold uppercase">Topics for this talk · {selected.length}</h3>
              {selected.map((t, i) => <article key={t.key} className="border rounded p-3 space-y-3">
                <div className="flex gap-3"><span className="font-mono text-[#FF5F15] font-bold">{String(i + 1).padStart(2, "0")}</span><div className="min-w-0 flex-1"><strong className="block break-words">{t.title || "Custom topic"}</strong><span className="text-xs text-zinc-500">{t.category}</span></div></div>
                <div className="flex flex-wrap gap-2"><Button variant="outline" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move topic ${i + 1} up`}>↑ Up</Button><Button variant="outline" disabled={i === selected.length - 1} onClick={() => move(i, 1)} aria-label={`Move topic ${i + 1} down`}>↓ Down</Button><Button variant="ghost" onClick={() => setSelected(items => items.filter((_, index) => index !== i))}>Remove</Button></div>
                {t.topic_id ? <details><summary className="cursor-pointer py-2 text-sm text-[#FF5F15] font-bold">View topic</summary><TopicContent content={t.content} attachments={t.attachments} /></details> : <div className="space-y-2"><label className="block text-xs">Custom topic title<Input value={t.title} onChange={e => editCustom(i, "title", e.target.value)} /></label><label className="block text-xs">Site-specific content<Textarea rows={3} value={t.overview} onChange={e => editCustom(i, "overview", e.target.value)} /></label><label className="block text-xs">Discussion points (one per line)<Textarea rows={3} value={t.discussion_points.join("\n")} onChange={e => editCustom(i, "discussion_points", e.target.value.split("\n"))} /></label><p className="text-xs text-zinc-500">This custom topic belongs only to this session.</p></div>}
              </article>)}
              {!selected.length && <p className="text-sm text-zinc-500">Add a library or custom topic to continue.</p>}
              <div className="flex flex-wrap gap-2"><Button variant="outline" onClick={() => setStep(1)}>+ Add topics</Button><Button variant="outline" onClick={startBlank}>+ Add custom topic</Button></div>
            </section>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Session title (optional)</Label>
                <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="talk-title" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Job</Label>
                <Select value={form.job_id} onValueChange={(v) => set("job_id", v)}>
                  <SelectTrigger data-testid="talk-job"><SelectValue placeholder="Select job" /></SelectTrigger>
                  <SelectContent>
                    {(jobs || []).map((j) => (
                      <SelectItem key={j.id} value={j.id}>{j.job_number} — {j.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Date</Label>
                <Input type="date" value={form.talk_date} onChange={(e) => set("talk_date", e.target.value)} data-testid="talk-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Start time</Label>
                <Input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} data-testid="talk-time" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-bold">Location</Label>
                <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Defaults to job site address" data-testid="talk-location" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Today's work</Label>
                <Textarea rows={2} value={form.work_activity} onChange={(e) => set("work_activity", e.target.value)} data-testid="talk-work" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Site-specific hazards</Label>
                <Textarea rows={2} value={form.site_specific_hazards} onChange={(e) => set("site_specific_hazards", e.target.value)} data-testid="talk-hazards" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Controls discussed</Label>
                <Textarea rows={2} value={form.site_specific_controls} onChange={(e) => set("site_specific_controls", e.target.value)} data-testid="talk-controls" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="talk-notes" />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Expected crew ({form.expected_attendee_ids.length})</Label>
              <div className="mt-1.5 grid sm:grid-cols-2 gap-1.5 max-h-40 overflow-y-auto p-2 rounded-lg border border-zinc-200 dark:border-white/10">
                {fieldWorkers.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer px-1.5 py-1 rounded hover:bg-zinc-100 dark:hover:bg-white/5" data-testid={`talk-attendee-${u.id}`}>
                    <Checkbox checked={form.expected_attendee_ids.includes(u.id)} onCheckedChange={() => toggleAttendee(u.id)} />
                    <span className="truncate">{u.name}</span>
                  </label>
                ))}
                {fieldWorkers.length === 0 && <div className="text-xs text-zinc-500">No team members found.</div>}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="sticky bottom-0 bg-white dark:bg-[#0D141C] border-t pt-3 flex-row flex-wrap justify-between sm:justify-between gap-2">
          {step === 2 ? (
            <Button variant="ghost" onClick={() => setStep(1)} className="uppercase text-xs font-bold tracking-wider" data-testid="talk-back">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
          ) : <button className="text-xs font-bold text-left py-3" onClick={() => setStep(2)}>{selected.length} TOPICS SELECTED · VIEW SELECTED</button>}
          {step === 2 && (
            <Button onClick={submit} disabled={saving || !selected.length}
              className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="talk-submit">
              <Check className="w-4 h-4 mr-1.5" /> {saving ? "Creating…" : "Create talk"}
            </Button>
          )}
          {step === 1 && (
            <Button disabled={!selected.length} onClick={() => setStep(2)} className="rounded-sm bg-[#FF5F15] uppercase text-xs font-bold tracking-wider text-white">
              Add {selected.length} topics <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
